import React, { useCallback } from 'react';

import { Box } from '@mui/material';
import { pendingChangesCounterStyles } from '@sharedCR/common/EntityFormModal/EntityFormModalHead/EntityFormModalHead.styles';
import { ErrorDotIndicator } from '@sharedCR/common/ErrorDotIndicator';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { PresenceSectionDot } from '@sharedCR/common/PresenceDot';
import { DeleteIcon, ResetIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';

import {
  getNavigationTabLabelStyles,
  labelButtonStyles,
  recordErrorIndicatorStyles,
} from './UpdateTableModal.styles';
import { UpdateTableModalTestIds } from './UpdateTableModal.testIds';

const TabLabel = ({
  label,
  id,
  onDeleteRecord,
  onRestoreRecord,
  isDeleted,
  withButton,
  isButtonDisabled = false,
  pendingChangesCount,
  hasErrors = false,
}) => {
  const onClick = useCallback(
    e => {
      e.stopPropagation();

      const action = isDeleted ? onRestoreRecord : onDeleteRecord;

      action(id);
    },
    [id, onDeleteRecord, onRestoreRecord, isDeleted],
  );

  return (
    <Box className="label" sx={getNavigationTabLabelStyles(isDeleted)}>
      {!!pendingChangesCount && (
        <PendingChangeWrapper
          isSmall
          className="pending-changes-count-wrapper"
          pendingChangesActionType={PendingChangesActionType.MODIFIED}
          sx={pendingChangesCounterStyles}
        >
          <p className="pending-changes-count">{pendingChangesCount}</p>
        </PendingChangeWrapper>
      )}

      {hasErrors && (
        <Box
          data-testid={UpdateTableModalTestIds.getRecordErrorIndicatorId(id)}
          sx={recordErrorIndicatorStyles}
        >
          <ErrorDotIndicator />
        </Box>
      )}

      <PresenceSectionDot sectionId={id} />

      {label}

      {withButton && (
        <Box
          className="label-button"
          data-testid={UpdateTableModalTestIds.getRecordActionButtonId(id)}
          disabled={isButtonDisabled}
          role="button"
          sx={labelButtonStyles}
          tabIndex="0"
          onClick={onClick}
        >
          {isDeleted ? <ResetIcon /> : <DeleteIcon />}
        </Box>
      )}
    </Box>
  );
};

TabLabel.propTypes = {
  id: PropTypes.string.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  withButton: PropTypes.bool.isRequired,
  label: PropTypes.node.isRequired,
  onDeleteRecord: PropTypes.func.isRequired,
  onRestoreRecord: PropTypes.func.isRequired,
  isButtonDisabled: PropTypes.bool,
  pendingChangesCount: PropTypes.number,
  hasErrors: PropTypes.bool,
};

export default TabLabel;
